/**
 * @fileoverview FlowGrid SDK - Browser Bundle
 * @module browser
 *
 * @description
 * Minimal script-tag facade over the unified FlowGrid SDK. Browser users get a
 * tiny top-level API for the common path, while advanced users can call
 * `FlowGrid.instance()` and use the full SDK namespaces.
 *
 * @example
 * ```html
 * <script src="https://cdn.flow-grid.xyz/flowgrid.min.js"></script>
 * <script>
 *   FlowGrid.init({ webId: 'your-web-id', apiKey: 'your-api-key' });
 *   FlowGrid.track('button_click', { button: 'signup' });
 *   FlowGrid.page();
 *   FlowGrid.identify('user_123', { plan: 'pro' });
 * </script>
 * ```
 */

import { FlowGrid, FlowGridInitConfig } from "./lib/flowgrid";
import type { ConsentCategory } from "./lib/client/transport";
import { wireDeclarativeBanners } from "./lib/consent/declarativeBanner";
import { wireDeclarativeAuth } from "./lib/features/core/declarativeAuth";
import type { RenderConsentBannerOptions } from "./lib/helpers/consent";
import { flags } from "./lib/features/core/flagsGlobal";
import { setFlagEngine } from "./lib/features/core/flagRegistry";
import {
  createFlagSource,
  readSeededFlags,
  type FlagSource,
  type FlagSourceOptions,
} from "./lib/features/core/flagSource";

type BrowserFacade = {
  init(config: FlowGridInitConfig): FlowGrid;
  instance(): FlowGrid | null;
  reset(): void;
  setConsent(consent: Partial<Record<ConsentCategory, boolean>>): void;
  hasConsent(category: ConsentCategory): boolean;
  autoTrack(options?: Parameters<FlowGrid["autoTrack"]>[0]): () => void;
  track: FlowGrid["track"];
  capture: FlowGrid["capture"];
  page: FlowGrid["page"];
  identify: FlowGrid["identifyUser"];
  signup: FlowGrid["signup"];
  login: FlowGrid["login"];
  defineFeature: FlowGrid["defineFeature"];
  wireConsentBanner(): void;
};

function getInstance(): FlowGrid {
  const instance = FlowGrid.instance();
  if (!instance) {
    throw new Error("FlowGrid not initialized. Call FlowGrid.init() first.");
  }
  return instance;
}

const browserFlowGrid: BrowserFacade = {
  init(config) {
    return FlowGrid.init(config);
  },

  instance() {
    return FlowGrid.instance();
  },

  reset() {
    FlowGrid.reset();
  },

  setConsent(consent) {
    FlowGrid.setConsent(consent);
  },

  hasConsent(category) {
    return FlowGrid.hasConsent(category);
  },

  autoTrack(options) {
    return getInstance().autoTrack(options);
  },

  track(eventName, properties, options) {
    return getInstance().track(eventName, properties, options);
  },

  capture(name, properties) {
    // Never throws, even before init — a micro-interaction must not break the page.
    return FlowGrid.instance()?.capture(name, properties) ?? Promise.resolve();
  },

  page(pathOrUrl, properties, options) {
    return getInstance().page(pathOrUrl, properties, options);
  },

  identify(userId, traits, anonymousId) {
    return getInstance().identifyUser(userId, traits, anonymousId);
  },

  // Programmatic signup/login from the script tag. The no-code form —
  // `data-fg-signup` / `data-fg-login` — is wired at the bottom of this file.
  signup(userId, method, source, properties) {
    return getInstance().signup(userId, method, source, properties);
  },

  login(userId, method, properties) {
    return getInstance().login(userId, method, properties);
  },

  defineFeature(featureId, featureName, opts) {
    return getInstance().defineFeature(featureId, featureName, opts);
  },

  // Manually (re-)scan the DOM for `[data-cookie-ccbanner]` consent markers.
  // The no-build path wires this automatically on load; SPA/programmatic users
  // call it after a client-side navigation re-renders the page. Independent of
  // FlowGrid.init() — consent must be able to gate before tracking starts.
  wireConsentBanner() {
    wireDeclarativeBanners({
      globalConfig:
        typeof window !== "undefined"
          ? (window.FlowGridConfig?.consentBanner as RenderConsentBannerOptions | undefined)
          : undefined,
    });
  },

};

declare global {
  interface Window {
    FlowGrid: BrowserFacade;
    flowgrid: BrowserFacade;
    /**
     * Optional full config for the no-build auto-init path. Set this *before*
     * the `<script>` tag to enable replay, autoTrack tuning, user identify,
     * consent, custom endpoint/transport, etc. without writing an `init()` call.
     * `webId` may be omitted here and supplied via `data-site` instead.
     */
    FlowGridConfig?: Partial<FlowGridInitConfig>;
    /**
     * Feature flags for a page with no build step.
     *
     * The npm entry exports the same functions; this is the script-tag mirror,
     * so a legacy template can register its own flag source without bundling
     * anything. See the assignment below.
     */
    FlowGridFlags?: BrowserFlagsApi;
    /**
     * Answers a server rendered into the page. Read automatically by
     * `window.FlowGridFlags.fromSeed()` and by `readSeededFlags()`.
     *
     * ```html
     * <script>window.__FLOWGRID_FLAGS__ = { "new-checkout": true };</script>
     * ```
     */
    __FLOWGRID_FLAGS__?: Record<string, boolean>;
  }
}

/** The flag surface a no-build page gets on `window.FlowGridFlags`. */
export interface BrowserFlagsApi {
  on(key: string, fallback?: boolean): boolean;
  off(key: string, fallback?: boolean): boolean;
  all(): Record<string, boolean>;
  ready(): Promise<void>;
  onChange(listener: () => void): () => void;
  /** Register a source: `{ endpoint }`, `{ initial }`, or both. */
  configure(options: FlagSourceOptions): FlagSource;
  /** Register a source seeded from `window.__FLOWGRID_FLAGS__`. */
  fromSeed(options?: FlagSourceOptions): FlagSource;
  /** Replace the answers by hand — for a page that pushes rather than fetches. */
  set(snapshot: Record<string, boolean>): void;
}

/**
 * Read init config from the loading `<script>` tag's data attributes, so a
 * no-build install can self-initialize:
 *
 * ```html
 * <script src="https://cdn.flow-grid.xyz/flowgrid.min.js" data-site="YOUR_WEBSITE_ID"></script>
 * ```
 *
 * Config sources, lowest → highest precedence:
 *   1. `window.FlowGridConfig` — a full {@link FlowGridInitConfig} object set
 *      *before* the tag loads. This is how the no-build path enables replay,
 *      autoTrack tuning, user identify, consent, custom transport, etc. — every
 *      option, declaratively, with no imperative `init()` call.
 *   2. `data-*` attributes on the tag (`data-site`/`data-website-id`/`data-web-id`,
 *      `data-api-key`, `data-endpoint`, `data-replay`).
 *
 * `data-manual` suppresses auto-init entirely (call `FlowGrid.init()` yourself).
 * `apiKey` is optional (web_id-only mode) but recommended. Returns `null` when
 * there's no resolvable web id or auto-init is suppressed, leaving the manual
 * `FlowGrid.init({...})` path fully in control.
 */
function readAutoInitConfig(): FlowGridInitConfig | null {
  const el =
    typeof document !== "undefined"
      ? (document.currentScript as HTMLScriptElement | null)
      : null;
  const attr = (name: string): string | null =>
    el && typeof el.getAttribute === "function" ? el.getAttribute(name) : null;

  // Programmatic users opt out of auto-init and configure everything in code.
  if (attr("data-manual") != null) return null;

  const fromGlobal =
    typeof window !== "undefined" ? window.FlowGridConfig : undefined;

  const webId =
    attr("data-site") ||
    attr("data-website-id") ||
    attr("data-web-id") ||
    fromGlobal?.webId;
  if (!webId) return null;

  // Spread the global config first so its replay/autoTrack/user/consent/transport
  // survive; data-* attributes (and the resolved webId) win where present.
  const config: FlowGridInitConfig = {
    ...fromGlobal,
    webId,
    apiKey: attr("data-api-key") ?? fromGlobal?.apiKey,
    endpoint: attr("data-endpoint") ?? fromGlobal?.endpoint,
  };

  // Simple on/off replay via attribute (the options-object form goes through
  // FlowGridConfig). Don't override an explicit FlowGridConfig.replay.
  const replayAttr = attr("data-replay");
  if (replayAttr != null && config.replay === undefined) {
    config.replay = replayAttr !== "false";
  }

  return config;
}

if (typeof window !== "undefined") {
  // `FlowGrid` is always the SDK facade. `flowgrid` (lowercase) may already be
  // claimed by the core flowgrid.js snippet (a different API) — don't clobber it.
  window.FlowGrid = browserFlowGrid;
  if (typeof window.flowgrid === "undefined") {
    window.flowgrid = browserFlowGrid;
  }

  // Flags, for a page that cannot import anything.
  //
  // The npm consumer writes `setFlagEngine(createFlagSource({ … }))`; a template
  // with a `<script>` tag and no bundler gets the identical engine here. Without
  // this the no-build path could read FlowGrid's own flags but had no way to
  // register its own source — so the oldest codebases, which are exactly the
  // ones most likely to already own a flag service, were the ones told to go
  // build the client themselves.
  //
  // ```html
  // <script>
  //   window.__FLOWGRID_FLAGS__ = { "new-checkout": true };   // optional SSR seed
  // </script>
  // <script src="https://cdn.flow-grid.xyz/flowgrid.js"></script>
  // <script>
  //   FlowGridFlags.fromSeed({ endpoint: "/api/my-flags" });
  //   if (FlowGridFlags.on("new-checkout")) renderNewCheckout();
  //   FlowGridFlags.onChange(render);
  // </script>
  // ```
  let browserSource: FlagSource | null = null;
  const register = (options: FlagSourceOptions): FlagSource => {
    browserSource = createFlagSource(options);
    setFlagEngine(browserSource);
    return browserSource;
  };

  window.FlowGridFlags = {
    // Reads go through the shared singleton, so they answer from whichever
    // engine is registered — a source configured here, or `FlowGrid.init()`.
    on: (key, fallback = false) => flags.on(key, fallback),
    off: (key, fallback = false) => flags.off(key, fallback),
    all: () => flags.all(),
    ready: () => flags.ready(),
    onChange: (listener) => flags.onChange(listener),
    configure: register,
    fromSeed: (options = {}) => register({ initial: readSeededFlags(), ...options }),
    set: (snapshot) => {
      (browserSource ?? register({})).set(snapshot);
    },
  };

  // Consent must be able to gate BEFORE any tracking, so wire declarative cookie
  // banners ahead of — and independently of — FlowGrid.init(). A
  // `<div data-cookie-ccbanner>` renders an opt-in banner that immediately flips
  // the transport gates to "blocked" (via setupConsent) until the visitor
  // accepts. Global defaults come from FlowGridConfig.consentBanner; per-element
  // data-cc-* override them. Suppress globally with `data-cc-manual` /
  // `data-no-consent-banner` on the script tag (or per element with data-cc-manual).
  const consentScript =
    typeof document !== "undefined" ? (document.currentScript as HTMLScriptElement | null) : null;
  const consentManual =
    (consentScript?.getAttribute?.("data-cc-manual") ?? consentScript?.getAttribute?.("data-no-consent-banner")) != null;
  if (!consentManual) {
    try {
      wireDeclarativeBanners({
        globalConfig: window.FlowGridConfig?.consentBanner as RenderConsentBannerOptions | undefined,
      });
    } catch {
      /* never throw from the bundle's top level */
    }
  }

  // Auto-init from FlowGridConfig / data-* (no-build path). Skipped entirely if
  // an instance already exists (a synchronous manual init won) or `data-manual`
  // is set — so full programmatic configuration is never locked out.
  const autoConfig = readAutoInitConfig();
  if (autoConfig && !FlowGrid.instance()) {
    try {
      FlowGrid.init(autoConfig);
    } catch {
      /* init surfaces its own error; never throw from the bundle's top level */
    }
  }

  // `<button data-fg-signup="google">` / `data-fg-login` — signups and logins
  // with no code. Reads the instance at click time, so a manual init() made
  // after this script loaded is picked up too.
  wireDeclarativeAuth(() => FlowGrid.instance());
}

export { FlowGrid };
export type { BrowserFacade };
export default browserFlowGrid;
